import React, { forwardRef } from "react";
import { Button as AntButton } from "antd";
import type { ButtonProps as AntButtonProps } from "antd";

import "./stylesButton.css";

export type ButtonIntent = "primary" | "secondary" | "neutral" | "danger";
export type ButtonVariant = "solid" | "outline" | "ghost";

const intentColorVar: Record<ButtonIntent, string> = {
  primary: "var(--COLOR-PRIMARY)",
  secondary: "var(--COLOR-SECONDARY)",
  neutral: "var(--COLOR-NEUTRAL_BUTTON_GRADIENT_1, var(--grey-750))",
  danger: "var(--COLOR-LOST_COLOR)",
};

const intentToAntType: Record<ButtonIntent, AntButtonProps["type"]> = {
  primary: "primary",
  secondary: "default",
  neutral: "default",
  danger: "primary",
};

/** antd Tag-style preset color names → hex. Lets `color="gold"` work. */
const ANTD_PRESETS: Record<string, string> = {
  gold: "#faad14",
  blue: "#1677ff",
  green: "#52c41a",
  purple: "#722ed1",
  red: "#ff4d4f",
  orange: "#fa8c16",
  cyan: "#13c2c2",
  magenta: "#eb2f96",
  yellow: "#fadb14",
  lime: "#a0d911",
  geekblue: "#2f54eb",
  volcano: "#fa541c",
};

const resolveColor = (color: string): string =>
  ANTD_PRESETS[color] ?? color;

export type ButtonProps = Omit<
  AntButtonProps,
  "type" | "icon" | "danger" | "iconPosition" | "color" | "variant"
> & {
  /**
   * Semantic color. Defaults to `"primary"`.
   *
   * - `primary` — main action (`--COLOR-PRIMARY`)
   * - `secondary` — branded alternative (`--COLOR-SECONDARY`)
   * - `neutral` — chrome / cancel / non-branded action (`--COLOR-NEUTRAL_BUTTON_GRADIENT_1`)
   * - `danger` — destructive action (`--COLOR-LOST_COLOR`)
   */
  intent?: ButtonIntent;
  /** Visual treatment. Defaults to `"solid"`. */
  variant?: ButtonVariant;
  /**
   * Escape hatch: any CSS color value (hex, var(--…), antd preset name).
   * Overrides `intent`'s color. Useful for one-off branded buttons.
   */
  color?: string;
  /**
   * Optional hover/focus color (hex, var(--…), antd preset name). When set,
   * it replaces the auto-derived hover treatment (opacity for solid/outline,
   * color-mix for ghost). Falls back to the base `color`/`intent` on rest.
   */
  hoverColor?: string;
  /**
   * Icon rendered to the left of the children. Mutually exclusive with
   * `iconRight` — antd `Button` only supports a single icon, so passing
   * both will trigger a dev-only warning and `iconLeft` wins.
   */
  iconLeft?: React.ReactNode;
  /**
   * Icon rendered to the right of the children. Mutually exclusive with
   * `iconLeft` — see `iconLeft` for details.
   */
  iconRight?: React.ReactNode;
};

const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    {
      intent = "primary",
      variant = "solid",
      color,
      hoverColor,
      iconLeft,
      iconRight,
      className = "",
      style,
      children,
      ...rest
    },
    ref,
  ) => {
    if (
      process.env.NODE_ENV !== "production" &&
      iconLeft != null &&
      iconRight != null
    ) {
      // antd Button accepts a single `icon` prop, so only one position can
      // render. Surface the silent drop early instead of letting it ship.
      // eslint-disable-next-line no-console
      console.warn(
        "[shared/Button] Both `iconLeft` and `iconRight` were passed; " +
          "antd only supports one icon per button. `iconLeft` wins, " +
          "`iconRight` is ignored.",
      );
    }

    const icon = iconLeft ?? iconRight;
    const iconPosition = iconRight && !iconLeft ? "end" : "start";
    const resolvedColor = color ? resolveColor(color) : intentColorVar[intent];
    const resolvedHoverColor = hoverColor ? resolveColor(hoverColor) : undefined;

    const inlineStyle: React.CSSProperties = {
      ...style,
      ["--pam-button-color" as string]: resolvedColor,
      ...(resolvedHoverColor
        ? {
            ["--pam-button-hover-color" as string]: resolvedHoverColor,
            ["--pam-button-hover-opacity" as string]: "1",
          }
        : {}),
    };

    return (
      <AntButton
        ref={ref as React.Ref<HTMLButtonElement>}
        type={intentToAntType[intent]}
        danger={intent === "danger"}
        icon={icon}
        iconPosition={iconPosition}
        className={`pam-button pam-button--${variant} pam-button--${intent} ${className}`.trim()}
        style={inlineStyle}
        {...rest}
      >
        {children}
      </AntButton>
    );
  },
);

Button.displayName = "Button";

export default Button;
